import { Link } from "react-router-dom";
import { ContentPageLayout } from "@/components/seo/ContentPageLayout";
import { SchemaMarkup } from "@/components/seo/SchemaMarkup";
import { FileText, Shield, Brain, Database, Users, Building, Clock, TrendingUp, CheckCircle } from "lucide-react";

const BASE = "https://sorvaine.com";

const faqItems = [
  { question: "What is bid management software?", answer: "Bid management software helps teams plan, write, review, and submit bids in one place — replacing scattered Word docs, email threads, and shared drives with a structured workflow." },
  { question: "How is bid management different from proposal management?", answer: "Bid management covers the full lifecycle of a competitive bid (qualification, pricing, compliance, submission). Proposal management is narrower and usually focused on writing and content reuse." },
  { question: "Who needs bid management software?", answer: "Any team responding to RFPs, ITTs, framework agreements, or formal tenders — typically agencies, consultancies, construction firms, IT vendors, and managed service providers." },
  { question: "Does Sorvaine handle pricing and commercials?", answer: "Yes. Sorvaine structures commercial sections as native pricing tables and lets you keep cost models alongside the rest of your bid content." },
  { question: "How quickly can a team get started?", answer: "Minutes. Upload a bid document and Sorvaine extracts the requirements, builds an outline, and drafts answers — no library setup required." },
  { question: "Is it suitable for public sector tenders?", answer: "Yes. Sorvaine maps every stated requirement and flags compliance gaps, which is critical for public sector bids where missing a single mandatory item disqualifies the response." },
];

export default function BidManagementSoftware() {
  return (
    <ContentPageLayout
      title="AI Bid Management Software for Winning Teams | Sorvaine"
      description="Sorvaine is bid management software that turns RFPs and tenders into submission-ready bids. Extract requirements, draft answers with AI, and track compliance in one workspace."
      canonical={`${BASE}/bid-management-software`}
      breadcrumbs={[
        { name: "Home", url: BASE },
        { name: "Bid Management Software", url: `${BASE}/bid-management-software` },
      ]}
      ctaHeading="See Sorvaine in action"
      ctaDescription="Book a 20-minute demo and see how your bid team can go from RFP to first draft in minutes."
    >
      <SchemaMarkup type="software" name="Sorvaine" description="AI-powered bid management software for teams responding to RFPs, ITTs, and tenders." url={`${BASE}/bid-management-software`} category="BusinessApplication" />
      <SchemaMarkup type="faq" items={faqItems} />

      <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-white leading-[1.1]">
        Bid Management Software
      </h1>
      <p className="mt-4 text-lg text-white/50 max-w-[620px]">
        Sorvaine is the AI bid management platform that replaces scattered Word docs, version chaos, and last-minute compliance scrambles with one structured workspace.
      </p>

      <section className="mt-16">
        <h2 className="text-2xl font-semibold tracking-tight text-white/90">The bid management problem</h2>
        <p className="mt-3 text-white/50 leading-relaxed">
          Most bid teams still run on Word, email, and a shared drive. Requirements get missed, content gets duplicated, and the final 48 hours before submission are spent reformatting instead of refining the win themes. The bigger the bid, the worse it gets.
        </p>
      </section>

      <section className="mt-14">
        <h2 className="text-2xl font-semibold tracking-tight text-white/90">How Sorvaine solves it</h2>
        <p className="mt-3 text-white/50 leading-relaxed">
          Upload the bid document. Sorvaine extracts every requirement, builds a structured outline, drafts each section using your knowledge vault, and tracks compliance live as your team edits. Submission day becomes a review, not a rebuild.
        </p>
      </section>

      <section className="mt-14">
        <h2 className="text-2xl font-semibold tracking-tight text-white/90">Core capabilities</h2>
        <div className="mt-6 grid sm:grid-cols-2 gap-5">
          {[
            { icon: FileText, title: "Requirement extraction", desc: "Pulls every question, criterion, and mandatory item from the source document automatically." },
            { icon: Shield, title: "Live compliance tracking", desc: "Maps your draft to each requirement so nothing slips through review." },
            { icon: Brain, title: "AI answer drafting", desc: "Generates first drafts in your tone of voice using your knowledge vault." },
            { icon: Database, title: "Self-building knowledge vault", desc: "Every completed bid feeds your library — searchable, reusable, and improving with each submission." },
          ].map((f) => (
            <div key={f.title} className="p-5 rounded-lg border border-white/[0.06] bg-white/[0.02]">
              <f.icon className="w-5 h-5 text-primary mb-3" />
              <h3 className="text-sm font-semibold text-white/80">{f.title}</h3>
              <p className="mt-1.5 text-sm text-white/40 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-14">
        <h2 className="text-2xl font-semibold tracking-tight text-white/90">Who uses Sorvaine</h2>
        <div className="mt-6 space-y-5">
          {[
            { icon: Building, title: "Agencies and consultancies", desc: "High-volume bid teams responding to enterprise and public sector RFPs." },
            { icon: Users, title: "Construction and engineering", desc: "Firms managing ITTs and framework submissions where compliance and pricing precision matter." },
            { icon: FileText, title: "IT and managed services", desc: "Vendors responding to security questionnaires, technical RFPs, and renewal bids." },
          ].map((u) => (
            <div key={u.title} className="flex gap-4 items-start">
              <u.icon className="w-5 h-5 text-primary mt-0.5 shrink-0" />
              <div>
                <h3 className="text-sm font-semibold text-white/80">{u.title}</h3>
                <p className="mt-1 text-sm text-white/40">{u.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-14">
        <h2 className="text-2xl font-semibold tracking-tight text-white/90">Why teams switch</h2>
        <div className="mt-6 space-y-4">
          {[
            { icon: Clock, text: "Cut bid response time by 40–60%. Reclaim hundreds of hours per year." },
            { icon: TrendingUp, text: "Win more by shipping consistent, on-message bids on every opportunity." },
            { icon: CheckCircle, text: "Never miss a mandatory requirement — automated compliance catches what humans overlook." },
          ].map((b, i) => (
            <div key={i} className="flex items-start gap-3">
              <b.icon className="w-5 h-5 text-primary mt-0.5 shrink-0" />
              <p className="text-sm text-white/50">{b.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-14">
        <h2 className="text-2xl font-semibold tracking-tight text-white/90">Compare Sorvaine</h2>
        <div className="mt-4 flex flex-wrap gap-3">
          <Link to="/tender-management-software" className="text-sm text-primary hover:underline">Tender Management Software →</Link>
          <Link to="/loopio-alternatives" className="text-sm text-primary hover:underline">Sorvaine vs Loopio →</Link>
          <Link to="/rfpio-alternatives" className="text-sm text-primary hover:underline">Sorvaine vs RFPIO →</Link>
          <Link to="/best-rfp-software" className="text-sm text-primary hover:underline">Best RFP Software 2026 →</Link>
        </div>
      </section>

      <section className="mt-14">
        <h2 className="text-2xl font-semibold tracking-tight text-white/90">Frequently asked questions</h2>
        <div className="mt-6 space-y-6">
          {faqItems.map((item, i) => (
            <div key={i}>
              <h3 className="text-sm font-semibold text-white/70">{item.question}</h3>
              <p className="mt-1.5 text-sm text-white/40 leading-relaxed">{item.answer}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-14">
        <h2 className="text-2xl font-semibold tracking-tight text-white/90">Learn more</h2>
        <ul className="mt-4 space-y-2">
          <li><Link to="/resources/what-is-rfp-response-software" className="text-sm text-primary hover:underline">What Is RFP Response Software?</Link></li>
          <li><Link to="/resources/how-to-respond-to-an-rfp" className="text-sm text-primary hover:underline">How to Respond to an RFP: Step by Step</Link></li>
          <li><Link to="/resources/proposal-content-library" className="text-sm text-primary hover:underline">Building a Winning Proposal Content Library</Link></li>
        </ul>
      </section>
    </ContentPageLayout>
  );
}
